import { useMutation, useQuery, useQueryClient, type Query } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { readExperimentPostStepFlags } from '~/features/experiments/store/experiment-post-step-flags.store';
import type { EnrichmentPostStepFlags } from '~/types/enrichment-flags';
import { extractApiError } from '~/utils/extract-api-error';

import {
  fetchAllExperimentPipelineEnrichedProducts,
  fetchContentScoringQualityScore,
  fetchContentScoringResults,
  fetchContentScoringStatus,
  fetchExperimentPipelineEnrichedProducts,
  fetchExperimentPipelineQualityScore,
  fetchExperimentPipelineStatus,
  fetchSampleEnrichmentPreview,
  fetchSampleEnrichmentQualityScore,
  fetchSampleEnrichmentStatus,
  submitExperimentPipelineApproval,
  triggerContentScoring,
  triggerExperimentPipeline,
  triggerSampleEnrichment,
} from '../services/content-scoring.service';
import {
  fetchMetaProductScoreDetails,
  fetchScheduledContentScoringQualityScore,
  fetchScheduledPipelineQualityScore,
  fetchScheduledSampleEnrichmentQualityScore,
  submitScheduledPipelineApproval,
} from '../services/scheduled-enrichment.service';
import type {
  ContentScoringStatus,
  ContentScoringTriggerPayload,
  ExperimentPipelineApprovalPayload,
  ExperimentPipelineStatus,
  ExperimentPipelineStatusResponse,
  ExperimentPipelineTriggerPayload,
  SampleEnrichmentStatus,
  SampleEnrichmentStatusResponse,
  SampleEnrichmentTriggerPayload,
} from '../types/content-scoring.types';
import type {
  MetaScoreStage,
  ScheduledPipelineApprovalPayload,
} from '../types/scheduled-enrichment.types';
import { scaleScore } from '../utils/scale-score';
import { seedPolledStatus } from '../utils/seed-polled-status';

// Content scoring drives a visible progress bar that updates with each
// poll, so it ticks fast. Sample enrichment is a slower job whose UI
// signal is the same per-row counter; 10s strikes the right cost/UX
// balance per product. Both stop on terminal status.
const CONTENT_SCORING_POLL_INTERVAL_MS = 10_000;
const SAMPLE_ENRICHMENT_POLL_INTERVAL_MS = 10_000;
const PIPELINE_POLL_INTERVAL_MS = 10_000;
/**
 * Hard wall-clock cap on any single polling session. Past this the hook
 * stops polling and surfaces `pollingTimedOut` so the page can show a
 * "Still running — check back later" affordance (per UX integration spec).
 */
const POLLING_DEADLINE_MS = 15 * 60 * 1000;
/**
 * Slow-cadence used while the polling endpoint keeps returning 5xx.
 * The spec asks for "switch to 10 s for the next two polls"; we already
 * run at 10 s baseline, so we slow further to 30 s to give the upstream
 * a chance to recover before resuming normal cadence on success.
 */
const POLLING_5XX_BACKOFF_MS = 30_000;
const TERMINAL_CONTENT_SCORING = new Set<ContentScoringStatus>(['COMPLETED', 'FAILED']);
const TERMINAL_SAMPLE_ENRICHMENT = new Set<SampleEnrichmentStatus>(['COMPLETED', 'FAILED']);
// AWAITING_APPROVAL parks the pipeline before delivery and is terminal for
// *polling* purposes — the user must act (approve/reject) to move it.
// REJECTED is the post-decline terminal state (status flips to CANCELLED
// alongside; pipeline itself never restarts from REJECTED). Without
// AWAITING_APPROVAL in this set step 4's polling would never stop and the
// UI would never advance to the Enrichment Overview where the Launch
// button lives; without REJECTED we'd keep polling after a decline.
const TERMINAL_PIPELINE = new Set<ExperimentPipelineStatus>([
  'AWAITING_APPROVAL',
  'COMPLETED',
  'REJECTED',
  'FAILED',
]);

/**
 * 15-minute polling deadline + derived `timedOut` flag. Restarts whenever
 * `resetKey` changes so callers can revive polling after a successful
 * Retry-on-FAILED trigger. Uses the React-docs "store info from previous
 * render" pattern (setState during render keyed on a tracked `resetKey`)
 * to avoid a sync setState in `useEffect`, which the project's
 * `react-hooks/set-state-in-effect` rule rejects.
 */
function usePollingDeadline(resetKey: number): { deadlineMs: number; timedOut: boolean } {
  const [deadlineMs, setDeadlineMs] = useState(() => Date.now() + POLLING_DEADLINE_MS);
  const [timedOut, setTimedOut] = useState(() => Date.now() >= deadlineMs);
  const [trackedResetKey, setTrackedResetKey] = useState(resetKey);

  // Render-phase reset: when the caller bumps resetKey, recompute deadline
  // and drop timedOut. React discards this render and re-runs with the
  // updated state. `Date.now()` lives inside the functional updater so the
  // impure call happens during commit, not render (satisfies the purity rule).
  if (resetKey !== trackedResetKey) {
    setTrackedResetKey(resetKey);
    setDeadlineMs(() => Date.now() + POLLING_DEADLINE_MS);
    setTimedOut(false);
  }

  useEffect(() => {
    if (timedOut) return;
    const remaining = deadlineMs - Date.now();
    const handle = setTimeout(
      () => {
        setTimedOut(true);
      },
      Math.max(remaining, 0),
    );
    return () => {
      clearTimeout(handle);
    };
  }, [deadlineMs, timedOut]);

  return { deadlineMs, timedOut };
}

/** True when the last fetch failed with a 5xx — used to switch to backoff cadence. */
function lastErrorWas5xx(error: unknown): boolean {
  return (
    error instanceof ApiError && (error.code === 'SERVER_ERROR' || (error.statusCode ?? 0) >= 500)
  );
}

/**
 * Builds the `refetchInterval` callback for a polling status query:
 *  - stops on terminal status,
 *  - stops once the 15 min deadline elapses,
 *  - slows to 30 s while the endpoint keeps returning 5xx.
 */
function buildPollInterval<TData>(opts: {
  intervalMs: number;
  deadlineMs: number;
  isTerminal: (data: TData | undefined) => boolean;
}): (query: Query<TData, unknown>) => number | false {
  return (query) => {
    if (Date.now() >= opts.deadlineMs) return false;
    if (opts.isTerminal(query.state.data)) return false;
    if (query.state.fetchFailureCount > 0 && lastErrorWas5xx(query.state.error)) {
      return POLLING_5XX_BACKOFF_MS;
    }
    return opts.intervalMs;
  };
}

interface TriggerContentScoringArgs {
  expId: string;
  /** Post-step flags to apply. Omit on a retry and the flags recorded at launch
   *  are read from the store, so a retry reproduces the original configuration
   *  instead of silently re-running with the steps off. */
  payload?: EnrichmentPostStepFlags;
}

/**
 * Mutation that kicks off the content-scoring workflow for an experiment.
 * Also used as the Retry CTA on the FAILED state — the success invalidation
 * forces the status query to refetch so the FailedStateCard swaps back to
 * the loading card automatically once the new run is observed.
 * Reads `localStorage('subBatchSize')` at fire time and passes it as an
 * optional override; absent or invalid values fall back to the server default.
 */
export function useTriggerContentScoring() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ expId, payload }: TriggerContentScoringArgs) => {
      const raw = localStorage.getItem('subBatchSize');
      const parsed = raw !== null ? parseInt(raw, 10) : NaN;
      const subBatchSize = !isNaN(parsed) && parsed > 0 ? parsed : undefined;
      // Launch passes the operator's choice explicitly; a retry passes nothing and
      // picks up what that experiment was launched with.
      const flags = payload ?? readExperimentPostStepFlags(expId);
      const body: ContentScoringTriggerPayload = {
        ...(subBatchSize !== undefined ? { subBatchSize } : {}),
        ...flags,
      };
      return triggerContentScoring(expId, Object.keys(body).length > 0 ? body : undefined);
    },
    onSuccess: (_data, { expId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.experiments.contentScoringStatus(expId),
      });
      // Also drop the previous quality-score snapshot so the empty-scoring
      // banner can clear (or re-arm) cleanly against the new run instead of
      // racing the stale 0-products payload back into the UI.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.experiments.contentScoringQualityScore(expId),
      });
      // Also refresh the experiment record — server stamps `experimentPhase`
      // on this trigger; the funnel guard reads phase to decide the canonical
      // step, so a stale cache would fight a freshly-fired CTA.
      void queryClient.invalidateQueries({ queryKey: queryKeys.experiments.detail(expId) });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to start content scoring. Please try again.'));
    },
  });
}

/**
 * Polls the content-scoring status while the workflow is in flight.
 * Stops on terminal status or after the 15-min deadline; slows to 30 s
 * on consecutive 5xx. The `pollingTimedOut` flag tells the caller to
 * show the "Still running — check back later" affordance. Pass a fresh
 * `resetKey` after a Retry-on-FAILED trigger to restart the deadline
 * window for the new run.
 */
export function useContentScoringStatus(expId: string, enabled = true, resetKey = 0) {
  const { deadlineMs, timedOut } = usePollingDeadline(resetKey);
  const query = useQuery({
    queryKey: queryKeys.experiments.contentScoringStatus(expId),
    queryFn: () => fetchContentScoringStatus(expId),
    enabled: !!expId && enabled,
    refetchInterval: buildPollInterval({
      intervalMs: CONTENT_SCORING_POLL_INTERVAL_MS,
      deadlineMs,
      isTerminal: (data) =>
        !!data?.scoringStatus && TERMINAL_CONTENT_SCORING.has(data.scoringStatus),
    }),
    // Keep polling even when the tab is backgrounded — users frequently
    // alt-tab during this multi-minute wait and expect to see fresh state
    // when they come back.
    refetchIntervalInBackground: true,
  });
  return { ...query, pollingTimedOut: timedOut };
}

/**
 * Paginated per-product scoring results for the Enrichment Overview
 * "Preview Products" tab. Columns are derived at render time from the
 * first product's `field_scores` array, so the table adapts automatically
 * to whatever fields the backend returns. `page` is zero-based.
 * staleTime + refetchOnWindowFocus: false — results are terminal (scoring
 * is COMPLETED by the time this is called) and re-fetching on focus would
 * reload product images needlessly.
 */
export function useContentScoringResults(
  expId: string,
  { page = 0, size = 20 }: { page?: number; size?: number } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.experiments.contentScoringResults(expId, page, size),
    queryFn: () => fetchContentScoringResults(expId, { page, size }),
    enabled: !!expId && enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Aggregate quality scores from the latest content-scoring run. Drives
 * the Catalog Content Quality Score gauge and Product Content Quality
 * Score bar chart on the Current Content Score page. One-shot query (no
 * polling) — scoring is terminal by the time this is called. Caller can
 * gate `enabled` on `scoringStatus === 'COMPLETED'` to avoid hitting the
 * endpoint before scoring has run.
 */
export function useContentScoringQualityScore(expId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.experiments.contentScoringQualityScore(expId),
    queryFn: () => fetchContentScoringQualityScore(expId),
    enabled: !!expId && enabled,
    // Backend returns catalogContentQualityScore on a 0–10 scale; the gauge
    // presents it out of 100.
    select: (data) => ({
      ...data,
      catalogContentQualityScore: scaleScore(data.catalogContentQualityScore),
    }),
  });
}

/**
 * Fetches aggregate quality scores for a scheduled (non-experiment) content-scoring
 * run via GET /enrichments/{workflowId}/content-scoring/quality-score.
 * Enabled only when workflowId is non-null. staleTime is intentionally finite —
 * Infinity causes TanStack Query v5 to skip the initial fetch when `enabled`
 * transitions false→true (the query is treated as perpetually fresh even with no data).
 */
export function useScheduledContentScoringQualityScore(workflowId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.scheduledEnrichment.contentScoringQualityScore(
      workflowId ?? '__disabled__',
    ),
    queryFn: () => {
      if (!workflowId) throw new Error('workflowId required');
      return fetchScheduledContentScoringQualityScore(workflowId);
    },
    enabled: !!workflowId && enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Backend returns catalogContentQualityScore on a 0–10 scale; the gauge
    // presents it out of 100.
    select: (data) => ({
      ...data,
      catalogContentQualityScore: scaleScore(data.catalogContentQualityScore),
    }),
  });
}

/**
 * Fetch Before/After/Lift quality scores from a completed scheduled sample-enrichment run.
 * Drives the Quality Score tab on the Review & Refine page. staleTime is intentionally
 * finite — Infinity causes TanStack Query v5 to skip the initial fetch when `enabled`
 * transitions false→true (the query is treated as perpetually fresh even with no data).
 */
export function useScheduledSampleEnrichmentQualityScore(
  workflowId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.scheduledEnrichment.sampleEnrichmentQualityScore(
      workflowId ?? '__disabled__',
    ),
    queryFn: () => {
      if (!workflowId) throw new Error('workflowId required');
      return fetchScheduledSampleEnrichmentQualityScore(workflowId);
    },
    enabled: !!workflowId && enabled,
    // Backend returns beforeScore/afterScore/lift on a 0–10 scale; the UI
    // presents them out of 100.
    select: (data) => ({
      ...data,
      beforeScore: scaleScore(data.beforeScore),
      afterScore: scaleScore(data.afterScore),
      lift: scaleScore(data.lift),
    }),
  });
}

/**
 * Fetch Before/After/Lift quality scores from a completed Meta pipeline run.
 * Drives the score comparison card in the Enrichment Overview agent panel.
 * staleTime is intentionally finite — Infinity causes TanStack Query v5 to skip
 * the initial fetch when `enabled` transitions false→true.
 */
export function useScheduledPipelineQualityScore(workflowId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.scheduledEnrichment.pipelineQualityScore(workflowId ?? '__disabled__'),
    queryFn: () => {
      if (!workflowId) throw new Error('workflowId required');
      return fetchScheduledPipelineQualityScore(workflowId);
    },
    enabled: !!workflowId && enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Backend returns beforeScore/afterScore/lift on a 0–10 scale; the UI
    // presents them out of 100. beforeScore/lift are absent in Meta Ads responses.
    select: (data) => ({
      ...data,
      beforeScore: scaleScore(data.beforeScore),
      afterScore: scaleScore(data.afterScore),
      lift: scaleScore(data.lift),
    }),
  });
}

interface SubmitScheduledApprovalArgs {
  workflowId: string;
  payload: ScheduledPipelineApprovalPayload;
}

/**
 * Mutation that records the operator's APPROVED/REJECTED decision while the
 * Meta pipeline is paused at AWAITING_APPROVAL. Wired to the Enrichment
 * Overview "Confirm & Publish" button.
 *
 * Error handling mirrors `useSubmitExperimentPipelineApproval`:
 *  - 409 Conflict → already actioned; surfaces a specific toast.
 *  - Other errors → generic toast.
 */
export function useSubmitScheduledPipelineApproval() {
  return useMutation({
    mutationFn: ({ workflowId, payload }: SubmitScheduledApprovalArgs) =>
      submitScheduledPipelineApproval(workflowId, payload),
    onError: (err) => {
      if (err instanceof ApiError && err.statusCode === 409) {
        toast.error(
          extractApiError(
            err,
            'This pipeline has already been actioned. Reload to see the latest status.',
          ),
        );
        return;
      }
      toast.error(extractApiError(err, 'Failed to publish. Please try again.'));
    },
  });
}

interface TriggerSampleEnrichmentArgs {
  expId: string;
  /** Optional — omit to let the server pick `sampleSize=10` and derive `useCase`. */
  payload?: SampleEnrichmentTriggerPayload;
}

/**
 * Mutation that kicks off the sample-enrichment workflow alongside content
 * scoring on the Review & Launch click. Accepts an optional payload so a
 * caller can override `sampleSize` or scope `useCase.fields` (GMC_ADS only);
 * the existing call site passes no payload to preserve server-default
 * behavior. Doubles as the Retry CTA from the FAILED state — the success
 * invalidation pushes the status query to refetch so the loading card
 * returns automatically.
 */
export function useTriggerSampleEnrichment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ expId, payload }: TriggerSampleEnrichmentArgs) => {
      // The sample must run under the same steps the full catalog will, or it
      // isn't a preview of it. Caller-supplied keys win.
      const body: SampleEnrichmentTriggerPayload = {
        ...readExperimentPostStepFlags(expId),
        ...payload,
      };
      return triggerSampleEnrichment(expId, Object.keys(body).length > 0 ? body : undefined);
    },
    onSuccess: (data, { expId }) => {
      // Optimistically record the new run's workflowId so the funnel layout's
      // auto-trigger guard (keyed off workflowId) flips immediately and never
      // re-fires — a stray re-fire clean-slates the sample back to 0. See
      // seedPolledStatus for why this seeds without invalidating.
      seedPolledStatus<SampleEnrichmentStatusResponse>(
        queryClient,
        queryKeys.experiments.sampleEnrichmentStatus(expId),
        (prev) => ({
          experimentId: expId,
          workflowId: data.workflowId,
          status: data.status,
          startedAt: prev?.startedAt ?? null,
          completedAt: null,
          totalProducts: prev?.totalProducts ?? data.productCount,
          succeededProducts: prev?.succeededProducts ?? 0,
          failedProducts: prev?.failedProducts ?? 0,
          failureReason: null,
        }),
      );
      // Refresh the experiment record so the funnel guard sees the new
      // `experimentPhase` (GENERATE_SAMPLE_CONTENT) before it next reads.
      void queryClient.invalidateQueries({ queryKey: queryKeys.experiments.detail(expId) });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to start sample enrichment. Please try again.'));
    },
  });
}

/**
 * Polls the sample-enrichment status while the workflow is in flight.
 * Mirrors `useContentScoringStatus` — same 15-min deadline and 5xx
 * backoff. Pass a fresh `resetKey` after a Retry-on-FAILED trigger to
 * restart the deadline window for the new run.
 */
export function useSampleEnrichmentStatus(expId: string, enabled = true, resetKey = 0) {
  const { deadlineMs, timedOut } = usePollingDeadline(resetKey);
  const query = useQuery({
    queryKey: queryKeys.experiments.sampleEnrichmentStatus(expId),
    queryFn: () => fetchSampleEnrichmentStatus(expId),
    enabled: !!expId && enabled,
    refetchInterval: buildPollInterval({
      intervalMs: SAMPLE_ENRICHMENT_POLL_INTERVAL_MS,
      deadlineMs,
      isTerminal: (data) => !!data?.status && TERMINAL_SAMPLE_ENRICHMENT.has(data.status),
    }),
    refetchIntervalInBackground: true,
  });
  return { ...query, pollingTimedOut: timedOut };
}

/**
 * Before/After/Lift + per-attribute quality scores for the latest sample
 * run. One-shot fetch (no polling) — step 3 is only reached after sample
 * enrichment is `COMPLETED`, so the snapshot is stable by the time this
 * runs. Drives the Quality Score tab of the Review & Refine page.
 */
export function useSampleEnrichmentQualityScore(expId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.experiments.sampleEnrichmentQualityScore(expId),
    queryFn: () => fetchSampleEnrichmentQualityScore(expId),
    enabled: !!expId && enabled,
    // Backend returns beforeScore/afterScore/lift on a 0–10 scale; the UI
    // presents them out of 100.
    select: (data) => ({
      ...data,
      beforeScore: scaleScore(data.beforeScore),
      afterScore: scaleScore(data.afterScore),
      lift: scaleScore(data.lift),
    }),
  });
}

interface SampleEnrichmentPreviewParams {
  page?: number;
  size?: number;
}

/**
 * Paginated per-product preview cards for the latest sample run. Drives
 * the Preview Products tab of the Review & Refine page. `page` defaults
 * to 0 and `size` to 10 (matches the standard 10-product sample run).
 * staleTime + refetchOnWindowFocus: false — sample run is COMPLETED by the
 * time this renders; re-fetching on focus reloads product images needlessly.
 */
export function useSampleEnrichmentPreview(
  expId: string,
  { page = 0, size = 10 }: SampleEnrichmentPreviewParams = {},
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.experiments.sampleEnrichmentPreview(expId, page, size),
    queryFn: () => fetchSampleEnrichmentPreview(expId, { page, size }),
    enabled: !!expId && enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

interface ExperimentPipelineEnrichedProductsParams {
  page?: number;
  size?: number;
}

/**
 * Paginated enriched products from the completed experiment pipeline run.
 * Drives the Enrichment Overview "Preview Products" tab for the GMC experiment
 * flow. staleTime + refetchOnWindowFocus: false — results are terminal by the
 * time this renders; re-fetching on focus reloads product data needlessly.
 */
export function useExperimentPipelineEnrichedProducts(
  expId: string,
  { page = 0, size = 10 }: ExperimentPipelineEnrichedProductsParams = {},
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.experiments.pipelineEnrichedProducts(expId, page, size),
    queryFn: () => fetchExperimentPipelineEnrichedProducts(expId, { page, size }),
    enabled: !!expId && enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Every enriched product of the completed experiment pipeline run, all server
 * pages folded into one response. Drives the client-paginating Enrichment Overview
 * listing; `useExperimentPipelineEnrichedProducts` stays the server-paged read.
 */
export function useAllExperimentPipelineEnrichedProducts(expId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.experiments.pipelineEnrichedProductsComplete(expId),
    queryFn: () => fetchAllExperimentPipelineEnrichedProducts(expId),
    enabled: !!expId && enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

interface TriggerExperimentPipelineArgs {
  expId: string;
  /**
   * Optional split overrides. The Review Groups & Outliers CTA passes
   * `zScoreDefault` = the confirmed sensitivity level's threshold so the
   * pipeline's split runs at the previewed sensitivity. The Delivery page's
   * retry passes `startFromStage: 'DELIVERY'`.
   */
  payload?: ExperimentPipelineTriggerPayload;
}

/**
 * Mutation that kicks off the end-to-end experiment pipeline. Fired from the
 * "Generate for Full Catalog" button (Review Groups & Outliers) and reused
 * as the Retry CTA on the Full Catalog FAILED state and the Delivery page
 * (`startFromStage: 'DELIVERY'`). The success invalidation pushes the
 * pipeline-status query to refetch so the loading card returns once the new
 * run is observed.
 * Reads `localStorage('subBatchSize')` at fire time and merges it into the
 * payload; absent or invalid values fall back to the server default.
 */
export function useTriggerExperimentPipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ expId, payload }: TriggerExperimentPipelineArgs) => {
      const raw = localStorage.getItem('subBatchSize');
      const parsed = raw !== null ? parseInt(raw, 10) : NaN;
      const subBatchSize = !isNaN(parsed) && parsed > 0 ? parsed : undefined;
      // Full-catalog run: carries the launch choice, so the catalog is enriched
      // with the steps the operator picked rather than the server default.
      // Caller-supplied keys win over the recorded ones.
      const mergedPayload: ExperimentPipelineTriggerPayload = {
        ...readExperimentPostStepFlags(expId),
        ...payload,
        ...(subBatchSize !== undefined ? { subBatchSize } : {}),
      };
      return triggerExperimentPipeline(
        expId,
        Object.keys(mergedPayload).length > 0 ? mergedPayload : undefined,
      );
    },
    onSuccess: (_data, { expId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.experiments.pipelineStatus(expId),
      });
      // Refresh the experiment record so the funnel guard sees the new
      // `experimentPhase` (FULL_CATALOG_GENERATION) and `status` (RUNNING).
      void queryClient.invalidateQueries({ queryKey: queryKeys.experiments.detail(expId) });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to start full-catalog pipeline. Please try again.'));
    },
  });
}

/**
 * Polls the experiment-pipeline status while the workflow is in flight.
 * Same 15-min deadline + 5xx backoff as the other status hooks. Pass a
 * fresh `resetKey` after a Retry-on-FAILED trigger to restart the
 * deadline window for the new run. Status carries no per-stage counters —
 * just the overall state and timestamps.
 */
/**
 * Aggregate Before/After/Lift + per-attribute + per-category quality scores
 * from the latest full-catalog pipeline run. One-shot fetch (no polling) —
 * the Enrichment Overview page is only reached after the pipeline reaches
 * AWAITING_APPROVAL / COMPLETED, so the snapshot is stable. Caller can gate
 * `enabled` on pipeline status to avoid hitting the endpoint prematurely.
 */
export function useExperimentPipelineQualityScore(expId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.experiments.pipelineQualityScore(expId),
    queryFn: () => fetchExperimentPipelineQualityScore(expId),
    enabled: !!expId && enabled,
    // Backend returns beforeScore/afterScore/lift on a 0–10 scale; the UI
    // presents them out of 100.
    select: (data) => ({
      ...data,
      beforeScore: scaleScore(data.beforeScore),
      afterScore: scaleScore(data.afterScore),
      lift: scaleScore(data.lift),
    }),
  });
}

/**
 * Optional `isTerminal` override lets callers extend the polling window past
 * the default status-based terminal set. Step-4 Full Catalog passes a
 * stages-aware predicate so polling continues past `AWAITING_APPROVAL` until
 * both `Enrichment` and `PostScoring` stages stamp `completed`.
 *
 * `indefinitePoll` decouples the poll-stop decision from the 15-min wall-clock
 * deadline. When `true`, the poll only stops when `isTerminal` returns `true`
 * — the deadline is set to `Infinity` inside `buildPollInterval`. The
 * `pollingTimedOut` UI flag still fires at 15 min (from `usePollingDeadline`)
 * so callers can show a "Still running — check back later" banner without
 * stopping the poll. Use this for flows where the backend job genuinely takes
 * longer than 15 min (e.g. full-catalog PostScoring over a large catalog).
 */
export function useExperimentPipelineStatus(
  expId: string,
  enabled = true,
  resetKey = 0,
  isTerminal?: (data: ExperimentPipelineStatusResponse | undefined) => boolean,
  indefinitePoll = false,
) {
  const { deadlineMs, timedOut } = usePollingDeadline(resetKey);
  const terminalCheck =
    isTerminal ?? ((data) => !!data?.status && TERMINAL_PIPELINE.has(data.status));
  const query = useQuery({
    queryKey: queryKeys.experiments.pipelineStatus(expId),
    queryFn: () => fetchExperimentPipelineStatus(expId),
    enabled: !!expId && enabled,
    refetchInterval: buildPollInterval({
      intervalMs: PIPELINE_POLL_INTERVAL_MS,
      // When indefinitePoll is set, pass Infinity so the deadline branch in
      // buildPollInterval never fires — only isTerminal can stop this poll.
      deadlineMs: indefinitePoll ? Infinity : deadlineMs,
      isTerminal: terminalCheck,
    }),
    refetchIntervalInBackground: true,
  });
  return { ...query, pollingTimedOut: timedOut };
}

interface SubmitApprovalArgs {
  expId: string;
  payload: ExperimentPipelineApprovalPayload;
}

/**
 * Mutation that records the operator's APPROVED/REJECTED decision while the
 * pipeline is paused at `AWAITING_APPROVAL`. Wired to the Enrichment
 * Overview "Launch Experiment" / "Reject" CTAs.
 *
 * Error handling:
 *  - 409 Conflict → one-shot signal already recorded; toasts a
 *    "this experiment has already been launched" message rather than the
 *    generic failure (per contract).
 *  - Other errors → generic toast via `extractApiError`.
 * On success: invalidates the pipeline-status and experiment queries so
 * the next read reflects the new server state. Caller handles navigation.
 */
export function useSubmitExperimentPipelineApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ expId, payload }: SubmitApprovalArgs) =>
      submitExperimentPipelineApproval(expId, payload),
    onSuccess: (_data, { expId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.experiments.pipelineStatus(expId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.experiments.detail(expId) });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.statusCode === 409) {
        toast.error(
          extractApiError(
            err,
            'This experiment has already been launched. Reload to see the latest status.',
          ),
        );
        return;
      }
      toast.error(extractApiError(err, 'Failed to launch experiment. Please try again.'));
    },
  });
}

/**
 * Fetches per-product Meta scoring detail for the drill-down modal.
 * Only enabled when both wfId and productId are non-null (i.e. the modal is open).
 * Returns undefined until enabled; 404 responses surface as a query error.
 */
export function useMetaProductScoreDetails(
  wfId: string | null,
  productId: string | null,
  stage: MetaScoreStage = 'FULL_POST',
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.scheduledEnrichment.metaProductScoreDetails(
      wfId ?? '__disabled__',
      productId ?? '__disabled__',
      stage,
    ),
    queryFn: () => {
      if (!wfId || !productId) throw new Error('wfId and productId required');
      return fetchMetaProductScoreDetails(wfId, productId, stage);
    },
    enabled: !!wfId && !!productId && enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Don't retry on 404 — no row exists for this product + stage combination.
      if (error instanceof ApiError && error.statusCode === 404) return false;
      return failureCount < 2;
    },
  });
}
